/**
 * PrChip + LinkedIssueChip — header atoms for a YAGNI-Code-tracked work item.
 *
 * `PrChip` renders the linked pull request (the `tickets.pr_url/pr_number`
 * columns) as a quiet "#9" link; it renders nothing
 * until a PR exists, so the header stays clean for items without one.
 *
 * `LinkedIssueChip` renders the one-way external-issue mirror (Linear / Jira)
 * this item was created from, as a small "YAG-7" link; nothing without a url.
 * Both are presentational — they take only the values the header already has.
 */
import { ExternalLink, GitPullRequest, Link2 } from "lucide-react";

export function PrChip({
  prUrl,
  prNumber,
}: {
  prUrl: string | null | undefined;
  prNumber?: number | null;
}) {
  if (!prUrl) return null;
  const label = typeof prNumber === "number" ? `#${prNumber}` : "PR";
  return (
    <a
      className="wk-pr-chip"
      href={prUrl}
      target="_blank"
      rel="noreferrer"
      title="View pull request on GitHub"
    >
      <GitPullRequest width={11} height={11} aria-hidden />
      {label}
      <ExternalLink width={10} height={10} aria-hidden />
    </a>
  );
}

export function LinkedIssueChip({
  url,
  label,
}: {
  url: string | null | undefined;
  label?: string | null;
}) {
  if (!url) return null;
  return (
    <a
      className="wk-issue-chip"
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open the linked issue"
    >
      <Link2 width={11} height={11} aria-hidden />
      {label && label.trim() ? label : "Issue"}
      <ExternalLink width={10} height={10} aria-hidden />
    </a>
  );
}
